import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { googleChallenge } from '../../auth/googleApi'
import { loadGoogleIdentity } from '../../auth/googleIdentity'
import { Button } from '../../components/ui'

export function GoogleSignInButton({ clientId, onCredential }: {
  clientId: string
  onCredential: (credential: string) => void
}) {
  const { t, i18n } = useTranslation()
  const container = useRef<HTMLDivElement>(null)
  const callback = useRef(onCredential)
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [attempt, setAttempt] = useState(0)
  useEffect(() => { callback.current = onCredential }, [onCredential])

  useEffect(() => {
    let active = true
    const element = container.current
    let submitted = false
    Promise.all([loadGoogleIdentity(), googleChallenge()]).then(([google, challenge]) => {
      if (!active || !element) return
      google.initialize({
        client_id: clientId, nonce: challenge.nonce, auto_select: false,
        callback: response => {
          if (!active || submitted) return
          submitted = true
          callback.current(response.credential)
        },
      })
      google.renderButton(element, {
        type: 'standard', theme: 'outline', size: 'large', text: 'continue_with',
        locale: i18n.language, width: Math.min(360, element.clientWidth || 280),
      })
      setStatus('ready')
    }).catch(() => { if (active) setStatus('error') })
    return () => { active = false; element?.replaceChildren() }
  }, [clientId, i18n.language, attempt])

  return <div style={{ display: 'grid', gap: 'var(--sp-3)', minWidth: 0 }}>
    <div ref={container} style={{ minWidth: 0, width: '100%', display: 'flex', justifyContent: 'center' }} />
    {status === 'loading' && <div role="status">{t('auth.google.loading')}</div>}
    {status === 'error' && <>
      <div role="alert">{t('auth.google.unavailable')}</div>
      <Button kind="secondary" onClick={() => { setStatus('loading'); setAttempt(value => value + 1) }}>{t('auth.google.retry')}</Button>
    </>}
  </div>
}
